import * as React from 'react'
import { CaretDown } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Accordion — compound expand/collapse component (AccordionRoot / AccordionItem
 * / AccordionTrigger / AccordionContent), adopted from the TailGrids accordion
 * model and built to match the {@link TabRoot} conventions (native, context-
 * driven, full ARIA — no extra dependency). Wired to First Bite tokens.
 *
 *   type        single | multiple    (one panel open at a time, or many)
 *   collapsible boolean              (single only — allow closing the open one)
 *   variant     contained | separated
 *
 * - contained (default): one rounded `gray-200` box; items divided by hairlines.
 * - separated: each item is its own bordered card with a gap between them.
 *
 * Each `AccordionTrigger` is a `<button>` inside an `<h3>` with `aria-expanded`
 * + `aria-controls`; each `AccordionContent` is a `role="region"` labelled by
 * its trigger. `data-state="open|closed"` is set on item/trigger/content for
 * styling. Arrow keys / Home / End move focus between headers; the panel height
 * animates (and respects `prefers-reduced-motion` via the transition).
 *
 * Controlled (`value` + `onValueChange`) or uncontrolled (`defaultValue`).
 * `value` is a string for `single`, a string[] for `multiple`.
 */

type AccordionType = 'single' | 'multiple'
type AccordionVariant = 'contained' | 'separated'

interface AccordionContextValue {
  openItems: string[]
  toggle: (value: string) => void
  variant: AccordionVariant
  baseId: string
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null)

function useAccordionContext(component: string): AccordionContextValue {
  const ctx = React.useContext(AccordionContext)
  if (!ctx)
    throw new Error(`<${component}> must be used within <AccordionRoot>`)
  return ctx
}

interface ItemContextValue {
  value: string
  open: boolean
  disabled: boolean
  triggerId: string
  regionId: string
}

const ItemContext = React.createContext<ItemContextValue | null>(null)

function useItemContext(component: string): ItemContextValue {
  const ctx = React.useContext(ItemContext)
  if (!ctx)
    throw new Error(`<${component}> must be used within <AccordionItem>`)
  return ctx
}

const toArray = (v: string | string[] | undefined): string[] =>
  v === undefined ? [] : Array.isArray(v) ? v : [v]

export interface AccordionRootProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'defaultValue'
> {
  type?: AccordionType
  /** single only — allow collapsing the currently-open item. Default true. */
  collapsible?: boolean
  variant?: AccordionVariant
  /** Open item(s) — uncontrolled. string for `single`, string[] for `multiple`. */
  defaultValue?: string | string[]
  /** Open item(s) — controlled. Pair with `onValueChange`. */
  value?: string | string[]
  onValueChange?: (value: string | string[] | undefined) => void
}

export const AccordionRoot = React.forwardRef<
  HTMLDivElement,
  AccordionRootProps
>(
  (
    {
      className,
      type = 'single',
      collapsible = true,
      variant = 'contained',
      defaultValue,
      value: valueProp,
      onValueChange,
      children,
      ...props
    },
    ref
  ) => {
    const baseId = React.useId()
    const [uncontrolled, setUncontrolled] = React.useState<string[]>(
      toArray(defaultValue)
    )
    const isControlled = valueProp !== undefined
    const openItems = isControlled ? toArray(valueProp) : uncontrolled

    const toggle = React.useCallback(
      (value: string) => {
        const isOpen = openItems.includes(value)
        let next: string[]
        if (type === 'multiple') {
          next = isOpen
            ? openItems.filter((v) => v !== value)
            : [...openItems, value]
        } else {
          next = isOpen ? (collapsible ? [] : openItems) : [value]
        }
        // No-op when nothing changed (single + non-collapsible re-click keeps
        // the same array): skip the setState and don't fire onValueChange.
        // `next` is always a fresh array in `multiple`, so a ref check suffices.
        const changed =
          type === 'multiple' ? next !== openItems : next[0] !== openItems[0]
        if (!changed) return
        if (!isControlled) setUncontrolled(next)
        // single mode: emit `undefined` (not '') when nothing is open, so
        // consumers can distinguish "no selection" from a real value.
        onValueChange?.(type === 'multiple' ? next : next[0])
      },
      [openItems, type, collapsible, isControlled, onValueChange]
    )

    const ctx = React.useMemo<AccordionContextValue>(
      () => ({ openItems, toggle, variant, baseId }),
      [openItems, toggle, variant, baseId]
    )

    // Roving focus across the headers (W3C accordion pattern).
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
      const triggers = Array.from(
        e.currentTarget.querySelectorAll<HTMLButtonElement>(
          '[data-accordion-trigger]:not([disabled])'
        )
      )
      if (triggers.length === 0) return
      const current = triggers.indexOf(
        document.activeElement as HTMLButtonElement
      )
      let next = current
      if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = triggers.length - 1
      else if (current === -1)
        next = e.key === 'ArrowDown' ? 0 : triggers.length - 1
      else if (e.key === 'ArrowDown') next = (current + 1) % triggers.length
      else next = (current - 1 + triggers.length) % triggers.length
      e.preventDefault()
      triggers[next]?.focus()
    }

    return (
      <AccordionContext.Provider value={ctx}>
        <div
          ref={ref}
          onKeyDown={handleKeyDown}
          className={cn(
            variant === 'contained'
              ? 'divide-y divide-fbui-gray-200 overflow-hidden rounded-lg border border-fbui-gray-200'
              : 'flex flex-col gap-3',
            className
          )}
          {...props}
        >
          {children}
        </div>
      </AccordionContext.Provider>
    )
  }
)
AccordionRoot.displayName = 'AccordionRoot'

export interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Unique key used to track this item's open state. */
  value: string
  disabled?: boolean
}

export const AccordionItem = React.forwardRef<
  HTMLDivElement,
  AccordionItemProps
>(({ className, value, disabled = false, children, ...props }, ref) => {
  const { openItems, variant, baseId } = useAccordionContext('AccordionItem')
  const open = openItems.includes(value)
  const itemCtx = React.useMemo<ItemContextValue>(
    () => ({
      value,
      open,
      disabled,
      triggerId: `${baseId}-trigger-${value}`,
      regionId: `${baseId}-region-${value}`,
    }),
    [value, open, disabled, baseId]
  )

  return (
    <ItemContext.Provider value={itemCtx}>
      <div
        ref={ref}
        data-state={open ? 'open' : 'closed'}
        className={cn(
          variant === 'separated' &&
            'overflow-hidden rounded-lg border border-fbui-gray-200',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </ItemContext.Provider>
  )
})
AccordionItem.displayName = 'AccordionItem'

export type AccordionTriggerProps =
  React.ButtonHTMLAttributes<HTMLButtonElement>

export const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ className, children, onClick, ...props }, ref) => {
  const { toggle } = useAccordionContext('AccordionTrigger')
  const { value, open, disabled, triggerId, regionId } =
    useItemContext('AccordionTrigger')

  return (
    <h3 className="flex">
      <button
        ref={ref}
        type="button"
        id={triggerId}
        data-accordion-trigger=""
        data-state={open ? 'open' : 'closed'}
        aria-expanded={open}
        aria-controls={regionId}
        disabled={disabled}
        onClick={(e) => {
          onClick?.(e)
          toggle(value)
        }}
        className={cn(
          'flex w-full items-center justify-between gap-4 px-4 py-3.5 text-left text-sm font-medium tracking-[-0.2px] text-fbui-gray-900 outline-none transition-colors',
          'enabled:hover:bg-fbui-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-default',
          'disabled:cursor-not-allowed disabled:text-fbui-text-disabled',
          className
        )}
        {...props}
      >
        {children}
        <CaretDown
          aria-hidden="true"
          weight="bold"
          className={cn(
            'size-4 shrink-0 text-fbui-gray-500 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
    </h3>
  )
})
AccordionTrigger.displayName = 'AccordionTrigger'

export type AccordionContentProps = React.HTMLAttributes<HTMLDivElement>

export const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ className, children, ...props }, ref) => {
  const { open, triggerId, regionId } = useItemContext('AccordionContent')
  return (
    <div
      role="region"
      id={regionId}
      aria-labelledby={triggerId}
      data-state={open ? 'open' : 'closed'}
      // `inert` when closed keeps the still-animating panel out of the tab order
      // and a11y tree, so collapsed content isn't focusable behind a 0fr row.
      inert={!open || undefined}
      className={cn(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}
    >
      <div className="overflow-hidden">
        <div
          ref={ref}
          className={cn('px-4 pb-4 pt-1 text-sm text-fbui-gray-700', className)}
          {...props}
        >
          {children}
        </div>
      </div>
    </div>
  )
})
AccordionContent.displayName = 'AccordionContent'
